import axios, { Axios } from "axios";
import { createGetWithLoadStatus, createPost } from "../../http/serviceClient";
import { User } from "../../domain/users";
import { LoginResult, LoginResults } from "./loginDialog";
import { getToken, setToken } from "../../http/tokenStore";
import { RequestStatus, SongRequest, SongRequests } from "../../domain/voting";
import { ReturnOrError } from "../../services/common";
import { DateTime } from "luxon";
import { Settings } from "../../domain/settings";

interface Configuration {
  songsAPIHostURL: string;
}

interface Approval {
  requestId: string;
  songId: string;
  value: number;
}

interface Denial {
  requestId: string;
  songId: string;
}

interface Archive {
  requestId: string;
  songId: string;
}

export interface AdminService {
  removeFromPlaylist: (id: string) => Promise<void>;
  moveUpOnPlaylist: (id: string) => Promise<void>;
  moveDownOnPlaylist: (id: string) => Promise<void>;
  login: (username: string, password: string) => Promise<LoginResult>;
  lockSong: (id: string) => Promise<void>;
  getSongRequests: (
    includeAll?: boolean,
  ) => Promise<ReturnOrError<SongRequests>>;
  approveSongRequest: (approval: Approval) => Promise<void>;
  denySongRequest: (denial: Denial) => Promise<void>;
  archiveSongRequest: (archive: Archive) => Promise<void>;
  deleteProcessedSongRequests: () => Promise<void>;
  unlockSong: (id: string) => Promise<void>;
  getSettings: () => Promise<ReturnOrError<Settings>>;
  saveSettings: (settings: Settings) => Promise<void>;
}

export const createAdminService = (
  configuration: Configuration,
  httpClient: Axios = axios,
  saveToken: (token: string) => void = setToken,
): AdminService => {
  const removeFromPlaylist = async (id: string) => {
    return createPost<void>(configuration, `/admin/${id}/remove`, httpClient)();
  };

  const moveUpOnPlaylist = async (id: string) => {
    return createPost<void>(configuration, `/admin/${id}/moveUp`, httpClient)();
  };
  const moveDownOnPlaylist = async (id: string) => {
    return createPost<void>(
      configuration,
      `/admin/${id}/moveDown`,
      httpClient,
    )();
  };
  const lockSong = async (id: string) => {
    return createPost<void>(configuration, `/lock/songs/${id}`, httpClient)();
  };

  const unlockSong = async (id: string) => {
    return createPost<void>(configuration, `/unlock/songs/${id}`, httpClient)();
  };

  interface LoginResponse {
    user?: User;
    token?: string;
  }

  const loginPost = createPost<LoginResponse>(
    configuration,
    "/admin/login",
    httpClient,
    getToken,
    () => {
      // TODO: this is either redundant or a potential bug
      console.log("Login failed");
    },
  );
  const login = async (
    username: string,
    password: string,
  ): Promise<LoginResult> => {
    const response = await loginPost({
      username,
      password,
    });

    const token = response?.token;
    if (token) {
      saveToken(token);
    }
    return token && response.user ? LoginResults.SUCCESS : LoginResults.FAILURE;
  };

  interface SongRequestJSON {
    requestedBy: User;
    song: { id: string; title: string };

    value: number;
    requestId: string;
    timestamp: string;
    status: string;
  }

  const getSongRequests: AdminService["getSongRequests"] = async (
    includeAll = false,
  ) => {
    // TODO: clean this up and create a better way to validate and convert types on JSON payloads
    const getSongRequestsJSON = createGetWithLoadStatus<{
      page: SongRequestJSON[];
    }>(
      configuration,
      includeAll
        ? "/admin/songRequests"
        : "/admin/songRequests?status=APPROVED&status=PENDING_APPROVAL",
      httpClient,
      getToken,
    );

    const jsonResponse = await getSongRequestsJSON();

    //TODO: better validation on JSON structure
    if (jsonResponse.status == "OK") {
      const page: SongRequest[] =
        jsonResponse.value?.page?.map((r) => {
          return {
            ...r,
            status: r.status as RequestStatus,
            timestamp: DateTime.fromISO(r.timestamp),
          };
        }) || [];

      return {
        value: {
          page,
        },
        status: "OK",
      };
    } else {
      return {
        value: undefined,
        status: jsonResponse.status,
      };
    }
  };

  const approveSongRequestPost = createPost<void>(
    configuration,
    "/admin/songRequest/approval",
    httpClient,
    getToken,
  );

  const denySongRequestPost = createPost<void>(
    configuration,
    "/admin/songRequest/denial",
    httpClient,
    getToken,
  );

  const archiveSongRequestPost = createPost<void>(
    configuration,
    "/admin/songRequest/archive",
    httpClient,
    getToken,
  );

  const approveSongRequest = async (request: Approval) => {
    return approveSongRequestPost(request);
  };

  const denySongRequest = async (denial: Denial) => {
    return denySongRequestPost(denial);
  };

  const archiveSongRequest = async (archive: Archive) => {
    return archiveSongRequestPost(archive);
  };

  const deleteProcessedSongRequests = async () => {
    const url = `${configuration.songsAPIHostURL}/admin/songRequests/processed`;
    const response = await httpClient.delete(url, {
      headers: { "x-token": `Bearer ${getToken()}` },
    });
    if (response.status >= 400) {
      throw new Error(
        `Unable to delete processed song requests: ${response.status}`,
      );
    }
  };

  const getSettings = createGetWithLoadStatus<Settings>(
    configuration,
    "settings",
    httpClient,
    getToken,
  );

  const saveSettings = async (settings: Settings) => {
    const url = `${configuration.songsAPIHostURL}/admin/settings`;
    const response = await httpClient.post(url, settings, {
      headers: { "x-token": `Bearer ${getToken()}` },
    });
    if (response.status >= 400) {
      throw new Error(`Unable to save settings: ${response.status}`);
    }
  };

  return {
    getSettings,
    saveSettings,
    removeFromPlaylist,
    moveUpOnPlaylist,
    moveDownOnPlaylist,
    login,
    lockSong,
    getSongRequests,
    approveSongRequest,
    archiveSongRequest,
    deleteProcessedSongRequests,
    unlockSong,
    denySongRequest,
  };
};
